import React, { Key, ReactNode, useCallback } from 'react';
import css from './tab-container.module.css';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import { route } from '../../../services/utils';

type TabContainerProps = {
  selectedTabKey: Key;
  setTab: Function;
  tabNavigation: React.ReactElement<TabNavigationProps>;
  tabContents: React.ReactElement<TabContentsProps>;
  theme?: string | false;
};

const TabContainerContext = React.createContext({
  selectedTabKey: null,
  setTab: null,
});

/**
 * A tab component which can be used for lazily loading and displaying contents.
 *
 * Usage:
 * - you'll need TabContainer as the root element, with some props:
 * - a TabNavigation component with all of the tab links
 * - a TabContents component with all of the tab contents
 *
 * See the component showcase for usage examples.
 */
export function TabContainer({
  selectedTabKey,
  setTab,
  tabNavigation,
  tabContents,
  theme = false,
}: TabContainerProps) {
  return (
    <TabContainerContext.Provider
      value={{ selectedTabKey: selectedTabKey, setTab: setTab }}
    >
      <div className={`${css['tab-container']} ${theme ? css[theme] : ''}`}>
        {tabNavigation}
        {tabContents}
      </div>
    </TabContainerContext.Provider>
  );
}

type TabNavigationProps = {
  children?:
    | React.ReactElement<TabLinkProps>
    | React.ReactElement<TabLinkProps>[];
  theme?: string | false;
};

/**
 * Tab navigation container which will have TabLinks inside of it.
 */
export function TabNavigation({ children, theme = false }: TabNavigationProps) {
  return (
    <ul className={css['tab-navigation'] + ' ' + (theme ? css[theme] : '')}>
      {children}
    </ul>
  );
}

type TabLinkProps = {
  tabKey: Key;
  disabled?: boolean;
  children?: ReactNode;
  theme?: string | false;
  onClick?: Function;
  count?: Number;
};

/**
 * Clickable tab link component, which can be enabled/disabled and styled.
 */
export function TabLink({
  tabKey,
  disabled = false,
  children,
  theme = false,
  onClick = null,
  count = null,
}: TabLinkProps) {
  const isCurrentTabSelected = (selectedTabKey: Key) => {
    return selectedTabKey === tabKey;
  };
  return (
    <TabContainerContext.Consumer>
      {(tabContainerContext) => (
        <li
          className={
            css['tab-link'] +
            ' ' +
            (theme ? css[theme] : '') +
            (disabled ? css['disabled'] : '') +
            ' ' +
            (isCurrentTabSelected(tabContainerContext.selectedTabKey)
              ? css['active']
              : '')
          }
          onClick={
            disabled || isCurrentTabSelected(tabContainerContext.selectedTabKey)
              ? null
              : () => {
                  tabContainerContext.setTab(tabKey);
                  onClick && onClick();
                }
          }
        >
          <span data-tabname={tabKey} className={css['tab-link-contents']}>
            {children}
          </span>

          {count === null ? (
            ''
          ) : (
            <div className="relative inline-block ml-2.5">
              <span className="px-2 py-1 text-xs font-bold leading-none text-white bg-[#d40000] rounded-full">
                {count.toString()}
              </span>
            </div>
          )}
        </li>
      )}
    </TabContainerContext.Consumer>
  );
}

type TabContentsProps = {
  children?: React.ReactElement<TabProps> | React.ReactElement<TabProps>[];
  theme?: string | false;
  classNames?: string;
  customClass?: string;
};

/**
 * Tab contents container which will have Tabs inside of it.
 */
export function TabContents({
  children,
  theme = false,
  customClass,
}: TabContentsProps) {
  return (
    <div
      className={`${css['tab-contents']} ${customClass ? customClass : ''} ${
        theme ? css[theme] : ''
      }`}
    >
      {children}
    </div>
  );
}

type TabProps = {
  tabKey: Key;
  children?: ReactNode;
};

/**
 * Tab component which will represent the actual contents of a tab.
 */
export function Tab({ tabKey, children }: TabProps) {
  return (
    <TabContainerContext.Consumer>
      {(tabContainerContext) => {
        let tabIsSelected = tabKey === tabContainerContext.selectedTabKey;
        if (!tabIsSelected) {
          return null;
        } else {
          return <div className={css['tab']}>{children}</div>;
        }
      }}
    </TabContainerContext.Consumer>
  );
}

/**
 * Used for gerenic tab switching in index lists.
 *
 * @param routePath Route path to go to on tab change
 * @param routeParams Route params that should be updated. [tab param, ...any other params that should be copied on tab change]
 */
export function useTab(
  routePath: string,
  routeParams: string[]
): [string, Function] {
  const history = useHistory();
  const params = useParams<{ [key: string]: string }>();
  const currentTab: Key = params[routeParams[0]];

  const setTab = useCallback(
    (newTab: string) => {
      const newRouteParams: { [key: string]: string } = {};

      routeParams.forEach((p) => {
        newRouteParams[p] = params[p];
      });

      history.push(
        route(routePath, { ...newRouteParams, [routeParams[0]]: newTab })
      );
    },
    [history, routePath, routeParams, params]
  );

  return [currentTab, setTab];
}
